import React, { useEffect, useState, useRef } from 'react';
import { Segment, Justify, Row, Col } from 'tea-component';
import '@/pages/home/assets/scss/page/home/Dashboard/index.scss';
import SvgIcon from '@/pages/home/components/SvgIcon';
import GlobalGames from './components/GlobalGames';
import GameSpotlight from './components/GameSpotlight';
import Announcements from './components/Announcements';
import UpcomingHoliday from './components/UpcomingHoliday';
import RecentlyViewed from './components/RecentlyViewed';
import DataSources from './components/DataSources';
import DataMap from './components/DataMap';
import MobileDataBrain from './components/MobileDataBrain';
import RecommendedPosition from './components/RecommendedPosition';
import LeaderBoard from './components/LeaderBoard';
import {
  getHomeUrl,
  getHotMobileGame,
  getHotPCGame,
  getHotConsoleGame,
} from '@/pages/home/services/home';
import { HomeUrlType } from '@/pages/home/types/home';
import { useIntl } from 'umi';
import { EntityType, DataSourceType, CardCacheType } from './_config/interface';
import {
  UPCOMING_GAMES,
  NEWRELEASEGAMES,
  NOTEWORTHYGAMES,
  BIGRISER,
} from './_config/constant';
import { PageLogMes } from '@/pages/home/utils/buriedPointCfg';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import {
  changeURLStatic,
  getDefaultDBIPath,
} from '@/pages/home/utils/urlConfig';
import Loading from '@/pages/dashboard/components/Loadings';
import { IS_GLOBAL } from '@/pages/home/_config/config';
import ExploreHeader from './components/ExploreHeader';
import TopPopularReports from '@home/pages/home/Explore/components/TopPopularReports';
import PubilcTrueDataCard from '@home/pages/home/Explore/components/PubilcTrueDataCard';
import NewReports from '@home/pages/home/Explore/components/NewReports';
import YearlyPoster from '@home/pages/home/Explore/components/YearlyPoster';
import MyShortcutsAndNewFeaturesCard from './components/MyShortcutsAndNewFeaturesCard';

export default function Explore(props: {
  // 是否展示2025年度表彰
  showYearlyPoster?: boolean;
  holidayExpanded?: boolean;
}) {
  const { showYearlyPoster, holidayExpanded } = props;

  const { locale, formatMessage } = useIntl(); // 中英切换方法
  const [itlSegVal, setItlSegVal] = useState<EntityType>(
    IS_GLOBAL ? 'pc' : 'mobile',
  );
  const [itlCardCacheData, setItlCardCacheData] = useState<CardCacheType>({});
  const itlCardCacheDataRef = useRef<Map<string, any>>(new Map());
  const [dataSourcesLists, setDataSourcesList] = useState<DataSourceType[]>([]);
  const [midUrlList, setMidUrlList] = useState<DataSourceType[]>([]); // 中间标题跳转url

  const [loading, setLoading] = useState(true);
  const [itlCarLoading, setItlCarLoading] = useState(true);
  const [itlCarError, setItlCarError] = useState(false);
  const [dataSourceLoading, setDataSourceLoading] = useState(true);
  const [dataSourceError, setDataSourceError] = useState(false);
  const [layoutConfigData] = useState<string[]>([
    NOTEWORTHYGAMES,
    NEWRELEASEGAMES,
    UPCOMING_GAMES,
    BIGRISER,
  ]);

  const getIntelligenceGameData = async () => {
    setLoading(true);
    try {
      setLoading(false);
      await getItlCarMsg(itlSegVal);
    } catch (err) {
      setLoading(false);
    }
  };

  useEffect(() => {
    /** 从接口获取点击标题跳转的链接 -- 建议前端写死*/
    getHomeUrlForTitle();
    /** 首次进来请求IntelligenceGame卡片内容 */
    getIntelligenceGameData();
    // 打点
    buriedPointPage(PageLogMes.dataBrainHome);
  }, []);

  /** 获取标题跳转的URL */
  const getHomeUrlForTitle = async () => {
    setDataSourceLoading(true);
    try {
      const urlList: HomeUrlType[] = await getHomeUrl({
        link_type: 0,
        info_type: 103,
      });
      // DataSources图标和标题跳转链接由前端静态配置获取，不用后端返回的链接
      const dataSourcesUrlLists =
        urlList?.filter((item) => item?.link_type === 2) || [];
      // 标题跳转链接
      const midUrlLists = urlList.filter((item) => item?.link_type === 3) || [];
      setDataSourcesList(dataSourcesUrlLists);
      setMidUrlList(midUrlLists);
      setDataSourceLoading(false);
      setDataSourceError(false);
    } catch (err) {
      setDataSourceLoading(false);
      setDataSourceError(true);
    }
  };

  /** 获取Intelligence卡片内容，并且实现缓存 */
  const getItlCarMsg = async (entityType: EntityType) => {
    setItlCarLoading(true);
    try {
      // 根据类型调用对应接口，每个接口一次返回全部 4 个卡片数据
      const fetchMap: Record<string, () => Promise<any>> = {
        mobile: getHotMobileGame,
        pc: getHotPCGame,
        console: getHotConsoleGame,
      };
      const fetchFn = fetchMap[entityType];
      const res = await fetchFn();
      const resData = res?.data || res || {};

      // 后端返回的 key 与前端常量的映射
      const KEY_MAP: Record<string, string> = {
        'noteworthy': NOTEWORTHYGAMES,
        '30-day new release': NEWRELEASEGAMES,
        '180-day upcoming': UPCOMING_GAMES,
        'big riser': BIGRISER,
      };

      const cardData: Record<string, any> = {};
      // 优先用后端 key 映射，同时保留 max_date
      Object.entries(KEY_MAP).forEach(([backendKey, frontendKey]) => {
        const raw = resData?.[backendKey] || resData?.[frontendKey] || {};
        cardData[frontendKey] = raw;
      });

      if (!itlCardCacheDataRef.current?.has(entityType)) {
        const hasError = Object.values(cardData).some((item: any) => item?.error);
        if (!hasError) {
          itlCardCacheDataRef.current?.set(entityType, cardData);
        }
        setItlCardCacheData(cardData);
      }
      setItlCarError(false);
      setItlCarLoading(false);
    } catch (err) {
      setItlCarError(true);
      setItlCarLoading(false);
    }
  };

  const changeSegment = (value: EntityType) => {
    if (itlCarLoading) {
      // 卡片数据请求中则禁止点击，避免渲染乱套
      return;
    }
    setItlSegVal(value);
    if (
      itlCardCacheDataRef?.current &&
      itlCardCacheDataRef.current.get(value)
    ) {
      const data = itlCardCacheDataRef.current.get(value);
      setItlCardCacheData(data);
    } else {
      getItlCarMsg(value);
    }
  };

  return (
    <Row gap={24} style={{ marginBottom: '-36px' }}>
      <Col span={18}>
        <ExploreHeader />
        <div className="dbh-module-line dbh-module-line--firstchild">
          <div
            className="dbh-module-title"
            onClick={() => {
              changeURLStatic('/dashboard');
            }}
          >
            {formatMessage({ id: 'dbh_dashboard' })}
            <SvgIcon className="dbh-module-title-icon" type="arrow-right" />
          </div>
        </div>
        {loading ? <Loading style={{ height: 'calc(60vh)' }} /> : null}

        <GlobalGames />

        <Justify
          className="dbh-module-line"
          left={
            <div
              className="dbh-module-title"
              onClick={() => {
                changeURLStatic(getDefaultDBIPath());
              }}
            >
              {formatMessage({ id: 'dbh_intelligence' })}
              <SvgIcon className="dbh-module-title-icon" type="arrow-right" />
            </div>
          }
          right={
            <Segment
              className="segment-light"
              rimless={false}
              value={itlSegVal}
              onChange={changeSegment}
              options={[
                {
                  text: formatMessage({ id: 'dbh_mobile' }),
                  value: 'mobile',
                },
                { text: formatMessage({ id: 'dbh_pc' }), value: 'pc' },
                {
                  text: formatMessage({ id: 'dbh_console' }),
                  value: 'console',
                },
              ]}
            />
          }
        />
        <Row>
          {layoutConfigData?.map((e) => {
            return (
              <Col span={12} key={e}>
                <GameSpotlight
                  loading={itlCarLoading}
                  error={itlCarError}
                  entityType={itlSegVal}
                  data={itlCardCacheData[e]}
                  cardName={e}
                  midUrlList={midUrlList}
                />
              </Col>
            );
          })}
        </Row>
        <Row className={'margin-t-10'}>
          <Col span={12}>
            <TopPopularReports />
          </Col>
          <Col span={12}>
            <PubilcTrueDataCard />
          </Col>
        </Row>
        {/* <span id="backHoner"></span> */}
        {/* DataBrain Game Data Lover Leaderboard - 2024 Q4 */}
        <LeaderBoard />
        {/* 2025年度表彰（此需求上线一个月后自动下架） */}
        {showYearlyPoster && <YearlyPoster locale={locale} />}
      </Col>
      <Col span={6}>
        <RecommendedPosition />
        {holidayExpanded && (
          <div className="dbh-holiday-expand-wrapper">
            <UpcomingHoliday />
          </div>
        )}
        <Announcements />
        <MyShortcutsAndNewFeaturesCard style={{ marginTop: 20, marginBottom: 20 }} />
        <NewReports />
        <RecentlyViewed />
        <DataSources
          loading={dataSourceLoading}
          error={dataSourceError}
          isNoData={!dataSourcesLists?.length}
          list={dataSourcesLists}
        />
        <DataMap />
        {!IS_GLOBAL && <MobileDataBrain />}
      </Col>
    </Row>
  );
}
